import React from "react";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { formatDate } from "../../../../utils/formatDate";
import { formatBR } from "../../../../utils/formatBR";
import { getQuoteStatusLabel } from "../statusOptions";
import {
  buildVersionLabel,
  hasVersions,
  isNotLowestPrice,
  findCotacaoSelecionada,
} from "services/quoteService";
import { podeLeiloar } from "../../GoBID/Leiloes/regras"; // PO1-3084 (épico PO1-3076)
import { primeiroNomeCriadoPor, tituloCriadoPor } from "../../../../utils/criadoPor"; // PO1-3488

// PO1-3084: selo "Em leilão" — oferta viva (aberta ou aguardando escolha).
const LEILAO_LABEL = { aberta: "Em leilão", encerrada: "Leilão: aguardando escolha" };

const STATUS_CLASS = {
  "Pendente":  "lc-badge-pendente",
  "Stand-by":  "lc-badge-standy",
  "Simulado":  "lc-badge-simulado",
  "Realizado": "lc-badge-realizado",
};

const STATUS_LOCK = {
  "Simulado":  { icon: "🔒", title: "Bloqueado para edição — crie uma nova versão" },
  "Realizado": { icon: "🔒", title: "Finalizado — nenhuma alteração permitida" },
};

const CotacaoListItem = ({ emb, selected, onSelect, onOpen, onCotar, onLeiloar, leilaoAtivo }) => {
  const statusClass = STATUS_CLASS[emb.statusEmbarque] || "lc-badge-pendente";
  // PO1-3084: "Leiloar" só em Pendente/Stand-by, na versão corrente e sem leilão vivo.
  const canLeiloar = Boolean(onLeiloar) && podeLeiloar(emb) && !leilaoAtivo;
  // PO1-3488: "Cotar" nas mesmas situações do "Leiloar" (ainda há o que cotar);
  // abre o modal já na seção "Frete e Transportadora".
  const canCotar = Boolean(onCotar) && podeLeiloar(emb) && !leilaoAtivo;
  const lock = STATUS_LOCK[emb.statusEmbarque] || null;
  const hasMix = Array.isArray(emb.veiculosMix) && emb.veiculosMix.length > 0;
  const origem = emb.origemCotacao || (hasMix ? "Manual" : "—");
  const cidade = [emb.cidadeDestino, emb.ufDestino].filter(Boolean).join(" / ");

  // PO1-2790: frete e prazo da transportadora selecionada, na listagem principal.
  const cotacaoSelecionada = findCotacaoSelecionada(emb);
  const freteSelecionado = cotacaoSelecionada
    ? Number(cotacaoSelecionada.valor)
    : Number(emb.valor_total || emb.valorFrete || 0);
  const prazoSelecionado = cotacaoSelecionada && cotacaoSelecionada.prazo != null
    ? cotacaoSelecionada.prazo
    : null;

  return (
    <div
      className={`lc-item${selected ? " selected" : ""}`}
      onClick={onSelect}
    >
      <div className="lc-item-main">
        {/* PO1-2863: nº sequencial da cotação (só o número, sem prefixo) */}
        {emb.numeroCotacao != null && (
          <span className="lc-badge lc-badge-cotacao" title="Nº da cotação">
            Nº {emb.numeroCotacao}
          </span>
        )}
        <span className="lc-item-embarque">{emb.numEmbarque || "—"}</span>
        {emb.codigoPedido && (
          <span
            className="lc-badge lc-badge-br"
            title="Número da BR (código do pedido)"
          >
            {formatBR(emb.codigoPedido)}
          </span>
        )}
        {hasVersions(emb) && (
          <span
            className="lc-badge lc-badge-versao"
            title="Esta cotação tem mais de uma versão"
          >
            {emb.versionLabel || buildVersionLabel(emb.versionNumber)}
          </span>
        )}
        <span className="lc-item-date">{formatDate(emb.dataEmbarque)}</span>
        <span className="lc-item-sep">·</span>
        <span className="lc-item-dest">{emb.cliente || "—"}</span>
        <span className="lc-item-cidade">{cidade || "—"}</span>
        <span className="lc-item-sep">·</span>
        <span className="lc-item-valor">{formatCurrency(emb.valorNF)}</span>
        <span className={`lc-badge ${statusClass}`}>{getQuoteStatusLabel(emb.statusEmbarque)}</span>
      </div>

      <div className="lc-item-secondary">
        <span className="lc-meta">
          Transportadora: <strong>{emb.transportadora || "—"}</strong>
        </span>
        <span className="lc-meta">
          Frete: <strong>{formatCurrency(freteSelecionado)}</strong>
        </span>
        <span className="lc-meta">
          Prazo: <strong>{prazoSelecionado != null ? `${prazoSelecionado}d` : "—"}</strong>
        </span>
        {/* PO1-3488: com usuário, "Criado por" no lugar de "Origem: Manual";
            integração (sem usuário) continua mostrando a origem. */}
        {emb.criadoPor ? (
          <span className="lc-meta" title={tituloCriadoPor(emb.criadoPor)}>
            Criado por: <strong>{primeiroNomeCriadoPor(emb.criadoPor)}</strong>
            {origem && origem !== "Manual" && origem !== "—" ? ` · ${origem}` : ""}
          </span>
        ) : (
          <span className="lc-meta">
            Origem: <strong>{origem}</strong>
          </span>
        )}
        <span className={`lc-meta-mix ${hasMix ? "sim" : "nao"}`}>
          Mix: {hasMix ? "Sim" : "Não"}
        </span>
        {emb.coletaSolicitada && (
          <span
            className="lc-badge lc-badge-coleta"
            title="Solicitação de coleta enviada à transportadora"
          >
            Coleta solicitada
          </span>
        )}
        {emb.coletaCancelada && (
          <span
            className="lc-badge lc-badge-coleta-cancelada"
            title="Coleta cancelada; a transportadora foi avisada"
          >
            Coleta cancelada
          </span>
        )}
        {/* PO1-2576: estados da coleta pela API */}
        {emb.coletaFalha && (
          <span
            className="lc-badge lc-badge-coleta-falha"
            title={(emb.coleta && emb.coleta.erro) || "A API da transportadora recusou a coleta"}
          >
            Falha na coleta
          </span>
        )}
        {emb.coletaAguardandoEtiqueta && (
          <span
            className="lc-badge lc-badge-coleta-aguardando"
            title="A coleta dos Correios é pedida depois de gerar a etiqueta na NF"
          >
            Coleta aguarda etiqueta
          </span>
        )}
        {emb.aprovacaoPendente && (
          <span
            className="lc-badge lc-badge-alcada"
            title="Aprovação acima do orçado aguardando decisão do aprovador"
          >
            Aguardando aprovação
          </span>
        )}
        {leilaoAtivo && (
          <span
            className="lc-badge lc-badge-leilao"
            title={`Leilão reverso nº ${leilaoAtivo.numero}. Clique para abrir.`}
            onClick={(e) => { e.stopPropagation(); if (onLeiloar) onLeiloar(emb, leilaoAtivo); }}
          >
            {LEILAO_LABEL[leilaoAtivo.estado] || "Em leilão"} · nº {leilaoAtivo.numero}
          </span>
        )}
        {isNotLowestPrice(emb) && (
          <span
            className="lc-badge lc-badge-preco"
            title="A transportadora selecionada não tem a menor proposta"
          >
            Não é o menor preço
          </span>
        )}
        {emb.aproximado && (
          <span
            title="Frete aproximado (estimativa)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              borderRadius: 999,
              padding: "3px 9px",
              fontSize: 11,
              background: "color-mix(in srgb, var(--everlog-status-warning) 12%, transparent)",
              color: "var(--everlog-status-warning)",
            }}
          >
            Aproximado
          </span>
        )}
        <div className="lc-item-actions lc-item-actions-row">
          {lock && (
            <span className="lc-item-lock" title={lock.title}>
              {lock.icon}
            </span>
          )}
          {canCotar && (
            <button
              className="lc-btn-cotar"
              title="Abrir a cotação nas propostas de frete (simular, Frete Spot, escolher transportadora)"
              onClick={(e) => { e.stopPropagation(); onCotar(emb); }}
            >
              Cotar
            </button>
          )}
          {canLeiloar && (
            <button
              className="lc-btn-leiloar"
              title="Abrir um leilão reverso de frete a partir desta cotação"
              onClick={(e) => { e.stopPropagation(); onLeiloar(emb, null); }}
            >
              Leiloar
            </button>
          )}
          <button
            className="lc-btn-open"
            onClick={(e) => { e.stopPropagation(); onOpen(emb); }}
          >
            Abrir
          </button>
        </div>
      </div>
    </div>
  );
};

export default CotacaoListItem;
